import * as THREE from 'three'
import Project from './Project'
import gsap from 'gsap'
import site from '../../../content/site.js'
import { lineTexture } from '../../Utils/localizedTexture.js'
import { t } from '../../../content/i18n.js'

export default class ProjectsSection
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.resources = _options.resources
        this.camera = _options.camera
        this.passes = _options.passes
        this.objects = _options.objects
        this.areas = _options.areas
        this.zones = _options.zones
        this.tiles = _options.tiles
        this.blockText = _options.blockText
        this.debug = _options.debug
        this.x = _options.x
        this.y = _options.y

        // Debug
        if(this.debug)
        {
            this.debugFolder = this.debug.addFolder('projects')
            this.debugFolder.open()
        }

        // Set up
        this.items = []

        this.interDistance = 24
        this.projectHalfWidth = 9
        this.rowCount = 2 // çift sıra: projeler iki paralel sıraya dönüşümlü dağılır
        this.rowDistance = 16 // ikinci sıra kuzeyde (kamera bu bölgede kuzeye bakar, iki sıra da görünür)

        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false
        this.container.updateMatrix()

        this.setMeshes()
        this.setList()
        this.setZone()
        this.setConnector()

        // Add all project from the list
        for(const _options of this.list)
        {
            this.add(_options)
        }
    }

    setMeshes()
    {
        this.meshes = {}

        // this.meshes.boardStructure = this.objects.getConvertedMesh(this.resources.items.projectsBoardStructure.scene.children, { floorShadowTexture: this.resources.items.projectsBoardStructureFloorShadowTexture })
        this.meshes.boardPlane = this.resources.items.projectsBoardPlane.scene.children[0]
        this.meshes.areaLabel = new THREE.Mesh(new THREE.PlaneGeometry(2, 0.5), new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0xffffff, alphaMap: lineTexture(() => t('floorOpen')) }))
        this.meshes.areaLabel.matrixAutoUpdate = false

        // Zemin baskıları için yuvarlak köşeli maske (kenar çizgisi yok)
        const canvas = document.createElement('canvas')
        canvas.width = 344
        canvas.height = 200
        const context = canvas.getContext('2d')
        context.fillStyle = '#000000'
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.fillStyle = '#ffffff'
        context.beginPath()
        context.roundRect(0, 0, canvas.width, canvas.height, 18)
        context.fill()
        this.meshes.printMask = new THREE.CanvasTexture(canvas)
    }

    setList()
    {
        // src/content/site.js → projects
        this.list = site.projects.map((_project) => ({
            name: _project.title,
            imageSources: (_project.images || []).map((_image) => `./projects/${_project.slug}/${_image}`),
            label: _project.label,
            link: _project.href
                ? { href: _project.href, x: - 4.8, y: - 3.3, halfExtents: { x: 3.2, y: 1.5 } }
                : null
        }))
    }

    setZone()
    {
        const columns = Math.ceil(this.list.length / this.rowCount)
        const rows = Math.min(this.rowCount, this.list.length)

        const zone = this.zones.add({
            position: {
                x: this.x + (columns - 1) * this.interDistance * 0.5,
                y: this.y + (rows - 1) * this.rowDistance * 0.5
            },
            halfExtents: {
                x: columns * this.interDistance * 0.5 + 3,
                y: (rows - 1) * this.rowDistance * 0.5 + 12
            },
            data: { cameraAngle: 'projects' }
        })

        zone.on('in', (_data) =>
        {
            this.camera.angle.set(_data.cameraAngle)
            gsap.to(this.passes.horizontalBlurPass.material.uniforms.uStrength.value, { x: 0, duration: 2 })
            gsap.to(this.passes.verticalBlurPass.material.uniforms.uStrength.value, { y: 0, duration: 2 })
        })

        zone.on('out', () =>
        {
            this.camera.angle.set('default')
            gsap.to(this.passes.horizontalBlurPass.material.uniforms.uStrength.value, { x: this.passes.horizontalBlurPass.strength, duration: 2 })
            gsap.to(this.passes.verticalBlurPass.material.uniforms.uStrength.value, { y: this.passes.verticalBlurPass.strength, duration: 2 })
        })
    }

    setConnector()
    {
        if(this.list.length < 2)
        {
            return
        }

        // Birinci sıradan ikinci sıraya giden yol (sıraların batı ucunda)
        this.tiles.add({
            start: new THREE.Vector2(this.x - this.projectHalfWidth, this.y + 2.5),
            delta: new THREE.Vector2(0, this.rowDistance - 5)
        })
    }

    add(_options)
    {
        const index = this.items.length
        const column = Math.floor(index / this.rowCount)
        const row = index % this.rowCount
        const x = this.x + column * this.interDistance
        const y = this.y + row * this.rowDistance

        // Create project
        const project = new Project({
            time: this.time,
            resources: this.resources,
            objects: this.objects,
            areas: this.areas,
            meshes: this.meshes,
            blockText: this.blockText,
            debug: this.debugFolder,
            x: x,
            y: y,
            ..._options
        })

        this.container.add(project.container)

        // Add tiles (aynı sıradaki bir önceki projeye)
        const previousProject = this.items[index - this.rowCount]
        if(previousProject)
        {
            const start = new THREE.Vector2(previousProject.x + this.projectHalfWidth, previousProject.y)
            const end = new THREE.Vector2(project.x - this.projectHalfWidth, project.y)
            const delta = end.clone().sub(start)
            this.tiles.add({
                start: start,
                delta: delta
            })
        }

        // Save
        this.items.push(project)
    }
}
